import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe } from "@stripe/stripe-js";

import { getStripeKey } from "./urls.helper";
import { logError } from "./logging";

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Loads Stripe.js on demand and caches the in-flight/resolved promise so all
 * payment UIs share one script load.
 *
 * Loading deliberately does NOT start at module/import time. Stripe.js used to
 * be requested on app boot (the dashboard/tray chunks called loadStripe at
 * module scope), so a boot-time network blip — common on mobile Safari — left a
 * rejected promise cached for the rest of the SPA session and a silently dead
 * card form if the member later reached checkout. Importing from
 * "@stripe/stripe-js/pure" avoids the package's inject-on-import side effect,
 * and clearing the cache on failure means the next payment UI mount retries
 * with a fresh script load (the pure loader also resets its own internal cache
 * after a failed load).
 *
 * Resolves to null instead of rejecting when Stripe.js can't be loaded, so
 * consumers can render a fallback rather than leak an unhandled rejection.
 */
const getStripePromise = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    stripePromise = loadStripe(getStripeKey() || "").catch((error) => {
      stripePromise = null;
      logError(error, { location: "getStripePromise" });
      return null;
    });
  }
  return stripePromise;
};

/**
 * Starts the Stripe.js load in the background, at browser idle time.
 *
 * Stripe recommends having Stripe.js present while customers browse (not just
 * at checkout) so Radar can collect fraud signals. The old module-scope loads
 * achieved that by accident but poisoned the whole session when a boot-time
 * load failed. This warm-up restores the early load without the failure mode:
 * it never competes with boot-critical work (idle callback), and a failure is
 * logged, resolves null and clears the cache — so the payment UI that actually
 * needs Stripe retries with a fresh load when it mounts.
 *
 * No-ops in automated browsers (our prerender function, crawlers): they abort
 * third-party requests, so loading would only generate error noise.
 */
const warmUpStripe = (): void => {
  // eslint-disable-next-line no-underscore-dangle
  if (window.__PRERENDER__ || window.navigator.webdriver) return;
  const startLoad = () => {
    getStripePromise();
  };
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(startLoad);
  } else {
    setTimeout(startLoad, 2000);
  }
};

export { getStripePromise, warmUpStripe };
